// SDM — IN INFINITUM · header labyrinthe (section `sdm-labyrinthe`).
//
// Adapté du prototype `demos/ux/def/main.js` (spec 2026-09-22 § 5) : la marche dans les couloirs, la
// navigation horizontale seule, la pilule à deux visages, la version noir ↔ blanc pilotée par le maximum du
// parcours et du défilement de page.
//
// Trois écarts avec le prototype, tous dus au passage sur une vraie boutique :
//   1. le catalogue vient du DOM (`[data-sdm-stations]`), rempli par Liquid depuis les produits ;
//   2. le bouton de la pièce est un vrai lien vers la page produit. Il ouvre la fiche de la boutique par-dessus
//      l'accueil (assets/sdm-fiche.js) à la fin de l'approche caméra (dolly), et la caméra recule à sa
//      fermeture ; sans ce module, il mène à la page produit ;
//   3. plus de panier simulé ni de vol de la vignette : le panier et la page produit sont ceux du thème.
//
// Le moteur 3D, lui, est le même — modules `sdm/lab/*` portés tels quels (tools/port-from-demo.mjs).
import * as THREE from 'three';
import { createEngine } from 'sdm/lab/engine';
import { buildAtlas, createProductLayer } from 'sdm/lab/products';
import { createThemeMixer, mixTheme } from 'sdm/theme-mixer';
import { createRoute } from 'sdm/route';
import { createGlass, GLASS_DEFAULTS } from 'sdm/glass';
import { nearestStop, shortestDelta, smoothstep, easeInOutCubic, wrap, clamp01 } from 'sdm/stops';

const root = document.documentElement;
const labEl = document.querySelector('[data-sdm-lab]');
const dataEl = document.querySelector('[data-sdm-stations]');
if (labEl && dataEl) mount();

function mount() {
  const STATIONS = JSON.parse(dataEl.textContent).slice(0, 8);
  if (!STATIONS.length) return;

  // Numéro au-dessus de la pièce : le texte saisi sur la station dans l'éditeur, tel quel (ex. « N°III », 0.4.124) ;
  // vide, rien (demande JB).
  const numero = (s) => (s.numero || '').trim();
  const byId = (id) => STATIONS.find((s) => s.id === id) ?? null;

  const GAIN = { dark: 1, light: 0.9 };   // luminosité des visuels dans la scène, noir / blanc
  const MIN_SPEED = 0.0015;               // fin d'inertie à vitesse constante lente : la caméra s'arrête vraiment
  // Vitesse maximale de la marche, en parcours par seconde (SDM.tasks.032) : l'approche reste exponentielle
  // (arrivée douce), mais plafonnée. Une pièce est à ~0,1 de la suivante : ~1,7 s de marche (0,8 s avant la
  // 0.4.48, 1,3 s avant le 24/09, jugé encore trop rapide par JB). Réglable en console (`sdmLab.maxSpeed = 0.2`).
  let maxSpeed = 0.07;
  // Boucle dans le noir (demande JB du 24/09) : le parcours n'y est pas régulier, une unité y couvre 2,5 à 4 fois
  // plus de distance que dans les couloirs, et la caméra y filait à plafond égal. Le plafond suit donc la
  // distance réelle : jamais plus de BOUCLE fois la vitesse des couloirs (mesurée au montage, `densiteRef`).
  // Réglable en console (`sdmLab.boucle = 1.5`).
  let boucle = 2;
  // Distance parcourue par unité de parcours autour de u (unités de la scène) ; la médiane sur la boucle entière
  // est celle des couloirs, qui en occupent l'essentiel.
  let densiteRef = 0;
  const densite = (u) => {
    const a = route.poseAt(wrap(u - 0.002)).pos, b = route.poseAt(wrap(u + 0.002)).pos;
    return Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) / 0.004;
  };
  const mediane = (v) => v.sort((x, y) => x - y)[v.length >> 1];
  const APPROCHE = 2.4;                   // raideur de l'approche exponentielle (3,2 avant la 0.4.48) : arrivée plus lente
  const GLISSE_VITESSE = 5;              // glissé à la souris (0.4.126) : vitesse maxi × 5 pendant le geste…
  const GLISSE_RAIDEUR = 12;             // …et approche plus raide : la caméra suit la main sans traîner
  const SNAP_DELAY = 250;                 // ms sans geste avant l'aimant

  const stageEl = labEl.querySelector('[data-sdm-stage]');
  const tagEl = labEl.querySelector('[data-sdm-tag]');
  const tocEl = labEl.querySelector('[data-sdm-toc]');
  const ctaEl = labEl.querySelector('[data-sdm-cta]');
  const annonceEl = labEl.querySelector('[data-sdm-annonce]');
  const consigneEl = labEl.querySelector('[data-sdm-consigne]');
  const pointsEl = labEl.querySelector('[data-sdm-points]');

  let lab = null, route = null, layer = null, mixer = null, stops = [0];

  /* ---------- éveil : la lumière arrive sur le labyrinthe (demande JB, 0.4.12) ----------
     Le preloader (snippets/sdm-preloader.liquid) couvre la page le temps que la 3D démarre. La scène attend
     derrière, dans le noir et la caméra en recul ; à sa sortie (`sdm:preloader-fin`), elle s'éclaire :
     l'exposition et le ciel montent, la tache de lumière glisse depuis la gauche en s'élargissant, un halo
     passe, et la caméra descend se poser sur la pochette. L'intro se joue à CHAQUE chargement (0.4.60, demande JB) :
     le preloader, lui, ne passe qu'une fois par session ; sans lui (rechargement, retour à l'accueil), elle démarre
     dès la première image rendue. Seules exceptions : animations réduites et éditeur de thème (scène éclairée). */
  const EVEIL = {
    duree: 2.4,      // s, de la sortie du preloader à la lumière pleine
    veille: 0.3,     // exposition de départ (part de la pleine) : le labyrinthe se devine déjà sous le voile
    recul: 0.35,     // départ de la caméra : en arrière de 35 % de la distance de visée…
    hauteur: 0.22,   // …et plus haut de 22 %
    glisse: -16,     // départ de la tache de lumière, en x (unités de la scène), vers sa place
    interface: 0.55, // part de l'éveil à laquelle l'interface commence son apparition (demande JB du 24/09 : plus tôt)
  };
  const avecPreloader = !!document.getElementById('sdm-preloader');   // retiré d'emblée s'il a déjà été vu dans la session
  const eveil = {
    t0: null,
    fini: matchMedia('(prefers-reduced-motion: reduce)').matches || !!window.Shopify?.designMode,
  };
  // Départ de l'intro. Le canvas reste masqué jusque-là (classe sdm-eveil, CSS) : au rechargement, sans preloader, le
  // moteur rend ses premières images en pleine lumière avant que l'éveil ne prenne la main, et le labyrinthe
  // s'affichait, disparaissait dans le noir, puis réapparaissait (signalé par JB, 0.4.64). L'historique d'accumulation
  // est remis à zéro pour ne rien garder de ces images claires.
  // L'intro attend aussi la première image de la 3D (classe `ready` du canvas, 0.4.134, signalé par JB) : quand la
  // scène tardait (vidéos chargées en même temps), le preloader partait à son maximum (6,5 s), l'intro courait ses
  // 2,4 s sur un canvas vide et la première image arrivait déjà en pleine lumière — intro perdue.
  let attenteImage = null;
  const eveiller = () => {
    if (eveil.t0 !== null) return;
    if (!stageEl.classList.contains('ready')) {
      if (!attenteImage) {
        attenteImage = new MutationObserver(() => {
          if (!stageEl.classList.contains('ready')) return;
          attenteImage.disconnect();
          eveiller();
        });
        attenteImage.observe(stageEl, { attributes: true, attributeFilter: ['class'] });
      }
      return;
    }
    eveil.t0 = performance.now();
    introLog('intro : départ');
    lab?.reset?.();
    labEl.classList.add('sdm-eveil');
  };
  // 3D en pause sous le preloader, une fois sa première image rendue (0.4.135, retour JB sur écran Retina) : elle
  // tournait à plein régime derrière le voile, la carte graphique saturée ne redessinait plus le fondu du preloader,
  // qui restait affiché pendant que l'intro se jouait dessous. Elle repart, et l'intro avec, une fois le fondu fini.
  let sousPreloader = avecPreloader && !window.SDM_PRELOADER_FINI;
  const PRELOADER_FONDU = 900;   // ms : fondu de sortie du preloader (snippets/sdm-preloader.liquid), puis retrait
  const liberer = () => {
    if (!sousPreloader) return;
    sousPreloader = false;
    syncPause();
    eveiller();
  };
  if (sousPreloader) {
    new MutationObserver((m, obs) => {
      if (!stageEl.classList.contains('ready')) return;
      obs.disconnect();
      syncPause();
    }).observe(stageEl, { attributes: true, attributeFilter: ['class'] });
  }
  if (!eveil.fini) {
    if (avecPreloader) {
      if (window.SDM_PRELOADER_FINI) eveiller();
      document.addEventListener('sdm:preloader-fin', () => setTimeout(liberer, PRELOADER_FONDU), { once: true });
    }
    // sans preloader : départ à la première image rendue (eveilFrame) ; secours dans tous les cas (le preloader
    // s'efface seul à 12 s au plus tard, la scène n'attend pas)
    setTimeout(() => { if (sousPreloader) liberer(); else eveiller(); }, 9000);
  } else if (sousPreloader) {
    document.addEventListener('sdm:preloader-fin', () => setTimeout(liberer, PRELOADER_FONDU), { once: true });
  }
  // Avancée de l'éveil au fil des IMAGES rendues, chacune comptée 1/10 s au plus (0.4.134, signalé par JB) : mesurée à
  // l'horloge, l'intro s'écoulait pendant les premières images, lentes (mise en route du rendu), et le labyrinthe
  // paraissait déjà éclairé. Relevé des temps en console : ?intro
  eveil.ecoule = 0;
  const eveilK = () => (eveil.fini ? 1 : eveil.t0 === null ? 0 : clamp01(eveil.ecoule / EVEIL.duree));
  const INTRO_LOG = new URLSearchParams(location.search).has('intro');
  let introPanneau = null;
  const introLog = (m) => {
    if (!INTRO_LOG) return;
    const ligne = `${Math.round(performance.now())} ms — ${m}`;
    console.info(`[intro] ${ligne}`);
    // relevé aussi à l'écran, en bas à gauche, pour une capture sans console (0.4.135)
    if (!introPanneau) {
      introPanneau = document.createElement('pre');
      introPanneau.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;margin:0;padding:8px 10px;max-width:calc(100vw - 16px);white-space:pre-wrap;font:11px/1.4 ui-monospace,monospace;color:#fff;background:rgb(0 0 0 / .75);pointer-events:none';
      document.documentElement.append(introPanneau);
    }
    introPanneau.textContent += (introPanneau.textContent ? '\n' : '') + ligne;
  };
  if (INTRO_LOG) {
    document.addEventListener('sdm:preloader-fin', () => {
      const v = performance.getEntriesByType('resource').filter((r) => /\/videos\//.test(r.name));
      introLog(`vidéos demandées avant la fin du preloader : ${v.filter((r) => r.startTime < performance.now()).length}`);
    });
  }
  if (INTRO_LOG) {
    const chargee = () => {
      introLog('page chargée (événement load)');
      // les 5 ressources les plus lentes, ce que le preloader a attendu
      const lentes = performance.getEntriesByType('resource').sort((a, b) => b.responseEnd - a.responseEnd).slice(0, 5)
        .map((r) => `${Math.round(r.responseEnd)} ms ${r.name.split('?')[0].split('/').pop()}`);
      introLog(`dernières ressources arrivées : ${lentes.join(' · ')}`);
    };
    if (document.readyState === 'complete') chargee(); else addEventListener('load', chargee, { once: true });
    document.addEventListener('sdm:preloader-fin', () => introLog(`fin du preloader (3D prête : ${stageEl.classList.contains('ready')})`));
    new MutationObserver(() => { if (stageEl.classList.contains('ready') && !eveil.logReady) { eveil.logReady = true; introLog('1re image 3D'); } })
      .observe(stageEl, { attributes: true, attributeFilter: ['class'] });
  }
  // Interface (annonce, pilule, flèches, points) : elle apparaît en fondu une fois le labyrinthe éveillé (0.4.57,
  // demande JB). Tout de suite sans éveil (éditeur, mouvement réduit) ; au pire au bout de 15 s.
  const interfacePrete = () => labEl.classList.add('sdm-hud-pret');
  if (eveil.fini) { interfacePrete(); labEl.classList.add('sdm-eveil'); }
  setTimeout(interfacePrete, 15000);
  // En-tête libéré si la page est défilée avant la fin de l'intro (0.4.124, signalé par JB) : au rechargement, le
  // navigateur rouvre la page plus bas, le labyrinthe hors écran ne rend rien, l'éveil n'avance pas et l'en-tête
  // restait caché jusqu'au secours des 15 s. Au-delà d'un demi-écran de défilement, il paraît (classe sdm-nav-libre).
  const libererNav = () => {
    const defile = Math.max(window.scrollY, document.querySelector('.page-wrapper')?.scrollTop || 0);
    if (defile > innerHeight * 0.5) {
      labEl.classList.add('sdm-nav-libre');
      removeEventListener('scroll', libererNav, true);
    }
  };
  addEventListener('scroll', libererNav, { passive: true, capture: true });   // capture : aussi le défilement de .page-wrapper
  addEventListener('pageshow', libererNav);
  libererNav();

  /* ---------- verre de la pilule ---------- */
  const glass = createGlass({
    defs: labEl.querySelector('[data-sdm-glass-defs]'),
    params: { ...structuredClone(GLASS_DEFAULTS), bezel: 18, depth: 40, chroma: 0.18, blur: 0.8, tint: 0.05, rim: 0.7, spec: 0.28 },
  });
  labEl.querySelectorAll('[data-refract]').forEach(glass.register);
  glass.applyStyleVars();

  /* ---------- la pilule et les flèches (SDM.tasks.021) ----------
     La pilule est le bouton de la pièce (plus de « Découvrir la collection » depuis la 0.4.79) ; les flèches
     des bords passent à la pièce précédente / suivante, comme le doigt ou le clavier. */
  tocEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-sdm-cta]')) { e.preventDefault(); enter(ctaEl.dataset.station); }
  });
  labEl.querySelectorAll('[data-sdm-pas]').forEach((b) => b.addEventListener('click', () => goStep(Number(b.dataset.sdmPas))));
  // La consigne de l'écran de départ fait comme la flèche droite : un clic ou un tap part vers la première pièce
  // (demande JB, 0.4.91). Seulement une fois bien visible : pendant son retard d'apparition, elle est déjà « on »
  // mais transparente, et un clic au centre de l'écran ne doit pas lancer la marche à l'insu du visiteur.
  consigneEl?.addEventListener('click', () => {
    if (consigneEl.classList.contains('on') && Number(getComputedStyle(consigneEl).opacity) > 0.3) goStep(1);
  });

  // « Agrandir la pièce » (réglage du bloc, 0-50 %) : la taille calculée par la route est bornée par les murs, le couloir
  // et le cadrage. L'agrandissement se fait vers le HAUT, pied fixe : le bas de la pièce reste au-dessus du cartel.
  function agrandir(spot, pct) {
    const k = 1 + Math.min(50, Math.max(-50, Number(pct) || 0)) / 100;   // négatif : la pièce rapetisse, pied fixe (0.4.129)
    if (k === 1) return spot;
    const [x, y, z] = spot.center;
    return { ...spot, height: spot.height * k, center: [x, y + (spot.height * (k - 1)) / 2, z] };
  }

  // « Décaler la hauteur » (réglage du bloc, -50 à +50 % de la hauteur de la pièce, 0.4.93) : monte (+) ou descend (-)
  // la pièce dans le couloir, pour caler un produit dont le visuel a plus ou moins de marge. Appliqué après
  // l'agrandissement : le pourcentage porte sur la taille affichée.
  function decaler(spot, pct) {
    const d = Math.min(50, Math.max(-50, Number(pct) || 0)) / 100;
    if (d === 0) return spot;
    const [x, y, z] = spot.center;
    return { ...spot, center: [x, y + spot.height * d, z] };
  }

  /* ---------- sortie du header : dézoom CSS, sans nouveau rendu ---------- */
  const scroll = { target: 0, pos: 0, lastInput: -1e9, snapped: true, scrub: 0 };   // scrub : multiplicateur de vitesse du glissé en cours (0 = aucun)
  const dolly = { k: 0, from: 0, to: 0, t0: 0, dur: 1600, active: false, center: null };
  const blocked = () => leaving || dolly.active || dolly.k > 0;
  let pageK = 0;
  let leaving = false;

  /* ---------- qui défile ? ----------
     Horizon ne fait pas défiler la FENÊTRE au-delà de 990 px : `html` et `body` y sont en `overflow: hidden`
     et c'est `.page-wrapper` qui défile (assets/base.css). En dessous, la fenêtre reprend la main. Le
     prototype, lui, n'avait affaire qu'à la fenêtre — d'où cette couche : sans elle, `scrollY` reste à 0 sur
     desktop, le header ne recule jamais et ses commandes flottent sur la collection.
     On écoute les DEUX, et on lit celui qui défile vraiment : la bascule se fait au redimensionnement, sans
     avoir à observer la requête média. */
  const wrapper = document.querySelector('.page-wrapper');
  const scrollTop = () => (wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper.scrollTop : scrollY);
  const HAUTEUR_ENTETE = 100;   // sections/sdm-header.liquid
  // Le bloc du bas est poussé dès que le bord de la collection arrive à 24 px sous la pilule, posée à 40 px du bas
  // (sections/sdm-labyrinthe.liquid) : décalage = défilement − (40 − 24).
  const BLOC_BAS = 0;   // le bloc du bas monte dès le premier pixel, comme la partie blanche (0.4.84)
  // Animations liées au défilement (CSS) : parallaxe et poussée du bloc du bas calculées par le navigateur, sans saccade.
  const DEFILEMENT_CSS = CSS.supports?.('animation-timeline: scroll()') ?? false;

  let sousEntete = false;
  const heroEl = labEl.querySelector('.sdm-hero');
  const AIDE_MARGE = 72;   // bord blanc à 72 px du bas de l'écran : le bouton Assistance (60 px) est posé sur le blanc

  function onScroll() {
    const top = scrollTop();
    pageK = clamp01(top / innerHeight);
    // Le dézoom de sortie se fait dans la caméra (cameraFrame), plus en CSS : un `scale` du canvas découvrait
    // ses bords, et le fond de la page derrière n'était pas raccord avec le rendu.
    // Le bloc du bas (annonce, cartel, pilule) et les flèches s'effacent dès qu'on commence à descendre, sur la moitié
    // d'un écran (0.4.56, plus progressif ; 20 % d'écran en 0.4.48). Au survol de chacun, il revient (CSS, 0.4.63).
    document.body.classList.toggle('sdm-hud-off', pageK > 0.01);
    // Le bloc du bas remonte avec la page au lieu de disparaître sur place (SDM.tasks.026). Il passe SOUS la
    // collection, et sa pilule n'est qu'à 40 px du bas : laissé fixe, il serait recouvert dès les premiers pixels.
    // Il est donc poussé par le bord de la collection (24 px d'écart), et s'efface dès les premiers pixels.
    // (repli : là où le navigateur lie les animations au défilement, la poussée et la parallaxe sont en CSS, voir la section)
    if (!DEFILEMENT_CSS) labEl.style.setProperty('--sdm-monte', `${Math.round(Math.max(0, top - BLOC_BAS))}px`);
    // Parallaxe (demande JB, 0.4.54) : le canvas, ses dégradés et les flèches remontent au QUART de la vitesse de la
    // page (0.4.55, distance divisée par 2 ; à mi-vitesse, la cible de la caméra restait pile au centre de la zone encore
    // visible, elle reste désormais un peu plus bas). Le bloc du bas, lui, reste poussé par le bord (--sdm-monte).
    // Valeur de repli : là où le navigateur lie les animations au défilement, c'est la section qui s'en charge (CSS).
    if (!DEFILEMENT_CSS) labEl.style.setProperty('--sdm-defile', `${Math.round(Math.min(top, innerHeight * 1.2))}px`);
    // fondu du bloc du bas et des flèches sur la moitié d'un écran (0.4.56) ; repli, sinon lié au défilement en CSS
    if (!DEFILEMENT_CSS) labEl.style.setProperty('--sdm-voile', (1 - clamp01(pageK / 0.5)).toFixed(3));
    // Le rendu est un canvas FIXE derrière toute la page : une fois le header sorti, il transparaîtrait
    // sous tout ce qui n'a pas de fond opaque — le pied de page du thème, par exemple. On l'efface.
    document.body.classList.toggle('sdm-lab-off', pageK >= 0.995);
    // Plus de passage au blanc en descendant (décision JB du 23/09, écart avec la spec § 5.1) : le labyrinthe
    // garde sa version et la collection passe par-dessus. Seul l'en-tête (transparent) change de ton, au moment
    // où la collection arrive sous lui — pas avant, il serait nuit sur du noir. Par sa PROPRE classe, et non par
    // `data-v` : celui-ci pilote aussi les dégradés de bord et le fond de la page, qui passeraient au blanc.
    sousEntete = top >= innerHeight - HAUTEUR_ENTETE;
    document.body.classList.toggle('sdm-lab-sous-entete', sousEntete);
    // Bouton « Assistance » (widget Zendesk, layout/theme.liquid) : caché sur le labyrinthe, il paraît quand la partie
    // blanche passe sous lui (demande JB, 0.4.114). Son iframe tient 60 px au bas de l'écran : on attend que le bord
    // blanc (bas du hero) soit à 72 px au-dessus du bas. Mesuré sur le hero, donc juste aussi quand `.page-wrapper` défile.
    root.classList.toggle('sdm-aide-visible', heroEl.getBoundingClientRect().bottom <= innerHeight - AIDE_MARGE);
    syncPause();
  }

  // Le moteur s'arrête dès que plus rien de lui n'est visible : header sorti de l'écran, ou fiche produit
  // par-dessus. La dernière image reste sur le canvas, il repart là où il en était.
  function syncPause() {
    lab?.setPaused?.((sousPreloader && stageEl.classList.contains('ready')) || pageK >= 0.995 || root.classList.contains('sdm-fiche-open'));
  }
  document.addEventListener('sdm:fiche-open', syncPause);
  document.addEventListener('sdm:fiche-close', syncPause);

  // Le ton des commandes suit la version. Le mixeur écrit lui aussi `data-v` quand son niveau change : on
  // repasse derrière lui à chaque image et on compare à la valeur RÉELLE du DOM plutôt qu'à un cache, qui
  // deviendrait faux dès la première écriture du mixeur. (Lire `dataset` ne force aucune mise en page.)
  function syncTon(blanc) {
    const v = blanc > 0.5 ? 'white' : 'black';
    if (root.dataset.v !== v) root.dataset.v = v;
  }

  addEventListener('scroll', onScroll, { passive: true });
  wrapper?.addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });
  root.dataset.v = 'black';
  onScroll();

  /* ---------- les gestes : HORIZONTAUX, et rien d'autre ----------
     Le vertical appartient à la page, qui descend vers la collection : aucun preventDefault sur ↑ ↓ ni sur
     la molette verticale, et `touch-action: pan-y` sur le hero. */
  const SWIPE_PX = 40;        // course horizontale au doigt qui fait changer de pièce
  const SWIPE_AXIS_PX = 12;   // course au bout de laquelle l'axe du geste est tranché, une fois pour toutes
  const SWIPE_WHEEL_PX = 80;  // même geste au pavé tactile : défilement horizontal cumulé
  const WHEEL_GESTE_MS = 300; // silence qui clôt un geste de molette (l'inertie de macOS envoie en continu)

  {
    // Un geste = une pièce (SDM.tasks.032), comme au doigt : une fois le pas fait, le reste du geste et son
    // inertie ne relancent rien. Le verrou tombe après WHEEL_GESTE_MS sans événement : c'est un nouveau geste.
    let acc = 0, at = 0, fait = false;
    addEventListener('wheel', (e) => {
      if (!inHeader() || e.target.closest?.('.lil-gui')) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;   // vertical : au navigateur
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
      const now = performance.now();
      if (now - at > WHEEL_GESTE_MS) { acc = 0; fait = false; }   // deux gestes séparés ne s'additionnent pas
      at = now;
      if (fait) return;
      acc += e.deltaX * unit;
      if (Math.abs(acc) > SWIPE_WHEEL_PX) { goStep(Math.sign(acc)); acc = 0; fait = true; }
    }, { passive: true });
  }

  // Molette verticale sur les commandes FIXES (bouton, points, flèches, annonce) : au-delà de 990 px c'est
  // `.page-wrapper` qui défile, et une molette posée sur un élément fixe remonte à la fenêtre, qui ne défile pas — la
  // page restait bloquée sous le curseur (signalé par JB, 0.4.65 ; ces zones captent la souris depuis le survol par
  // zone). On la relaie au défileur. Horizontale : inchangée (pas-à-pas ci-dessus).
  labEl.querySelectorAll('.sdm-toc, .sdm-points, .sdm-pas, .sdm-annonce, .sdm-consigne').forEach((el) => el.addEventListener('wheel', (e) => {
    if (!wrapper || wrapper.scrollHeight <= wrapper.clientHeight) return;   // la fenêtre défile : rien à relayer
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
    e.preventDefault();
    wrapper.scrollBy({ top: e.deltaY * unit, behavior: 'instant' });
  }, { passive: false }));

  /* ---------- glissé contrôlé, souris et doigt (0.4.126 souris, 0.4.127 doigt, demande JB) ----------
     Pendant le geste, la caméra suit la main le long du parcours (GLISSE_PX de course pour une étape) ; au
     relâchement, elle repart dans le sens du dernier mouvement jusqu'à l'arrêt suivant (pièce ou écran de départ).
     Geste trop court (moins de SWIPE_PX) : retour à l'arrêt de départ. */
  const GLISSE_PX = () => innerWidth * 0.4;   // course pour passer d'une étape à la suivante
  // Arrêt suivant dans le sens `dir` depuis la position `u` (strictement devant).
  const arretSuivant = (u, dir) => {
    let best = Infinity;
    for (const st of stops) {
      let d = shortestDelta(u, st) * dir;
      if (d <= 1e-4) d += 1;
      best = Math.min(best, d);
    }
    return best * dir;
  };
  // g = { x0, x, depart, sens } ; renvoie false si rien ne peut glisser (3D absente ou navigation bloquée)
  // Au doigt (0.4.128, retour JB : un glissé vif accélérait trop) : vitesse plafonnée plus bas qu'à la souris et
  // course bornée à une étape par geste, dans un sens comme dans l'autre.
  const DOIGT_VITESSE = 2, DOIGT_COURSE = 1;
  const glisseDebut = (g, x, doigt = false) => {
    if (!lab || blocked()) return false;
    g.x0 = x; g.x = x; g.depart = scroll.target; g.sens = 0;   // la course compte d'ici : pas de saut au démarrage
    g.doigt = doigt;
    scroll.scrub = doigt ? DOIGT_VITESSE : GLISSE_VITESSE;
    couperCartel();
    return true;
  };
  const glisseSuivre = (g, x) => {
    if (x !== g.x) g.sens = x < g.x ? 1 : -1;   // vers la gauche = en avant
    g.x = x;
    let etapes = (g.x0 - g.x) / GLISSE_PX();
    if (g.doigt) etapes = Math.max(-DOIGT_COURSE, Math.min(DOIGT_COURSE, etapes));
    scroll.target = g.depart + etapes * (1 / (STATIONS.length + 1));
    scroll.snapped = true;
    scroll.lastInput = performance.now();
  };
  const glisseFin = (g) => {
    scroll.scrub = 0;
    const course = g.x0 - g.x;
    if (Math.abs(course) <= SWIPE_PX || !g.sens) scroll.target = g.depart;   // geste trop court : retour
    // au doigt, une seule pièce par geste : l'arrêt suivant compté depuis le départ, si le geste finit dans le
    // sens de la course ; sinon retour au départ
    else if (g.doigt) scroll.target = Math.sign(course) === g.sens ? g.depart + arretSuivant(g.depart, g.sens) : g.depart;
    else scroll.target += arretSuivant(scroll.target, g.sens);
    scroll.snapped = true;
    scroll.lastInput = performance.now();
    couperCartel();
  };

  {
    // L'axe est tranché au premier centimètre et figé jusqu'à la levée. À la verticale on ne fait RIEN, le
    // navigateur descend la page tout seul : un glissé de travers n'emmène jamais sur une autre pièce.
    let last = null;
    addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1 || !inHeader() || e.target.closest?.('button, a, .lil-gui')) { last = null; return; }
      const { clientX: x, clientY: y } = e.touches[0];
      last = { x, x0: x, y0: y, axis: null };
    }, { passive: true });
    addEventListener('touchmove', (e) => {
      if (!last) return;
      const { clientX: x, clientY: y } = e.touches[0];
      if (!last.axis) {
        const dx = Math.abs(x - last.x0), dy = Math.abs(y - last.y0);
        if (dx < SWIPE_AXIS_PX && dy < SWIPE_AXIS_PX) return;   // geste encore trop court pour trancher
        last.axis = dx > dy ? 'x' : 'y';
        if (last.axis === 'x' && !glisseDebut(last, x, true)) last.axis = 'y';   // rien à faire glisser : geste ignoré
      }
      if (last.axis === 'x') glisseSuivre(last, x);   // la caméra suit le doigt (0.4.127)
    }, { passive: true });
    const leverDoigt = () => {
      if (last?.axis === 'x') glisseFin(last);
      last = null;
    };
    addEventListener('touchend', leverDoigt, { passive: true });
    addEventListener('touchcancel', leverDoigt, { passive: true });
  }

  {
    // Glissé à la souris, sur ordinateur (cliquer-glisser latéral, demande JB 0.4.79 ; contrôlé 0.4.126) : axe tranché
    // au premier centimètre. Bouton maintenu, la caméra suit la souris le long du parcours (GLISSE_PX de course pour
    // une étape) ; au relâchement, elle repart dans le sens du dernier mouvement jusqu'à l'arrêt suivant (pièce ou
    // écran de départ). Glissé trop court : retour à l'arrêt de départ. Pas de sélection de texte pendant le geste,
    // et le clic qui suit un glissé est avalé (relâché sur un bouton, il ne l'active pas).
    let drag = null;
    addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !inHeader() || !e.target.closest?.('[data-sdm-lab]')
        || e.target.closest('button, a, .lil-gui')) { drag = null; return; }
      e.preventDefault();   // ni sélection de texte ni glisser natif d'image
      drag = { x: e.clientX, x0: e.clientX, y0: e.clientY, axis: null };
    });
    addEventListener('pointermove', (e) => {
      if (!drag || e.pointerType !== 'mouse') return;
      if (!drag.axis) {
        const dx = Math.abs(e.clientX - drag.x0), dy = Math.abs(e.clientY - drag.y0);
        if (dx < SWIPE_AXIS_PX && dy < SWIPE_AXIS_PX) return;
        drag.axis = dx > dy ? 'x' : 'y';
        if (drag.axis === 'x') {
          if (glisseDebut(drag, e.clientX)) root.classList.add('sdm-glisse');
          else drag.axis = 'y';   // rien à faire glisser : le geste est ignoré
        }
      }
      if (drag.axis === 'x') glisseSuivre(drag, e.clientX);
    });
    const avaler = (c) => { c.stopPropagation(); c.preventDefault(); removeEventListener('click', avaler, true); };
    const lacher = () => {
      if (drag?.axis === 'x') {
        glisseFin(drag);
        addEventListener('click', avaler, true);
        // le clic suit le relâchement dans la même tâche ; s'il ne vient pas (relâché hors de la fenêtre), on
        // n'en avale pas un plus tard
        setTimeout(() => removeEventListener('click', avaler, true), 0);
      }
      root.classList.remove('sdm-glisse');
      drag = null;
    };
    addEventListener('pointerup', lacher);
    addEventListener('pointercancel', lacher);
  }

  const REGLAGES = new URLSearchParams(location.search).has('reglages');
  addEventListener('keydown', (e) => {
    if (e.target.closest?.('input, textarea, select') || e.metaKey || e.ctrlKey || e.altKey) return;
    if (!inHeader()) return;
    // ← → : la pièce précédente / suivante. ↑ ↓ et Page Up/Down restent au navigateur : elles descendent la
    // page vers la collection, seul usage du vertical.
    if (e.key === 'ArrowRight') { e.preventDefault(); goStep(1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goStep(-1); }
    // Panneau de réglages (G) : outil de mise au point, fermé aux visiteurs (demande JB du 24/09, 0.4.39). Il ne
    // s'ouvre qu'avec `?reglages` dans l'adresse.
    if (REGLAGES && e.key.toLowerCase() === 'g') lab?.toggleGui();
  });

  // Les gestes ne pilotent le labyrinthe que tant qu'il est à l'écran : plus bas, la page appartient à la
  // collection, et un défilement horizontal y sert au carrousel, pas à la caméra.
  // Fiche ouverte par-dessus : le labyrinthe ne répond plus, ses touches et gestes sont à elle.
  const inHeader = () => pageK < 0.5 && !root.classList.contains('sdm-fiche-open');

  /* ---------- moteur ---------- */
  (async () => {
    try {
      lab = await createEngine({
        canvas: stageEl, theme: 'dark', camera: 'artwork-02', autoPlay: false, bodyTheme: false,
        onFrame: (dt) => { if (layer) { mixer.frame(); dollyFrame(); cameraFrame(dt); eveilFrame(dt); } },
        onCamera: () => { if (layer) labelFrame(); },
        gui: { title: 'Header labyrinthe — réglages', themeSwitch: false, cameraSelect: false, path: false },
      });
      route = createRoute(lab);
      stops = route.stops;   // 0 = vue globale (la pochette), puis les emplacements le long de la marche
      densiteRef = mediane(Array.from({ length: 200 }, (_, i) => densite((i + 0.5) / 200)));
      if (pointsEl) {
        pointsEl.replaceChildren(...STATIONS.map((s, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', numero(s) ? `${numero(s)} — ${s.nom}` : s.nom);
          b.addEventListener('click', () => goStation(i));
          return b;
        }));
        pointsEl.hidden = false;
      }
      // Cases de l'atlas des pièces (SDM.tasks.028, flou) : sur ordinateur Retina, une pièce vue de près couvre 650 à
      // 900 px de rendu, plus que ses ~590 px dans une case de 640 (agrandie, donc floue) → 1024. Sur mobile (rendu 1,25×)
      // elle n'en couvre que ~300 : 640 suffit. Dans la limite du GPU (4 colonnes). ?cell=N pour comparer.
      const cellParam = Number(new URLSearchParams(location.search).get('cell'));
      const maxCell = Math.floor((lab.renderer.capabilities.maxTextureSize || 4096) / 4);
      const cell = Math.min(cellParam >= 256 ? cellParam : lab.isMobile ? 640 : 1024, 2048, maxCell);
      const atlas = await buildAtlas(STATIONS.map((s) => s.image), cell);
      // chaque pièce est posée debout dans le couloir de son emplacement, centrée à hauteur des yeux et
      // tournée vers la caméra de sa station : toujours vue de face, et reflétée par les miroirs.
      layer = createProductLayer(lab, atlas, STATIONS.map((s, i) => ({
        id: s.id, cell: i, ...decaler(agrandir(route.spot(i, atlas.rects[i].aspect), s.agrandir), s.decaler),
      })));
      if (labEl.dataset.reflets === 'false') layer.setReflections(false);
      mixer = createThemeMixer(lab, { level: 0, onLevel: (l) => {
        layer.setGain(GAIN.dark + (GAIN.light - GAIN.dark) * l);
        // Les dégradés de bord suivent le niveau à l'image près (SDM.tasks.030), au même poids que la 3D
        // (niveau^1,5, voir sdm-theme-mixer.js) : `data-v` ne bascule qu'à mi-chemin, puis 1,6 s de transition.
        labEl.style.setProperty('--sdm-lvl', (l ** 1.5).toFixed(3));
      } });
    } catch (err) {
      console.warn('[sdm] rendu 3D indisponible (WebGL 2)', err);
      labEl.classList.add('sdm-no-webgl');
      interfacePrete();   // sans 3D, pas d'éveil à attendre
      return;
    }
    // Le moteur monte une orbite libre (outil de mise au point, touche G) et OrbitControls pose au passage
    // « touch-action: none » en ligne sur le canvas. Le labyrinthe tient tout le premier écran : sans cela
    // le doigt tomberait sur le canvas et la page ne descendrait plus.
    stageEl.style.touchAction = '';
    syncPause();
  })();

  /* ---------- caméra : inertie, aimant, approche ---------- */
  const camPos = new THREE.Vector3(), camLook = new THREE.Vector3(), eveilD = new THREE.Vector3();

  // Dézoom de sortie : le champ s'élargit à mesure que la page descend, du même facteur que l'ancien `scale(0.92)`
  // en CSS (tan(fov/2) ÷ 0,92). Le moteur y voit un mouvement de caméra et rend en conséquence, comme en marche.
  const dezoom = (fov) => (pageK > 0
    ? 2 * THREE.MathUtils.radToDeg(Math.atan(Math.tan(THREE.MathUtils.degToRad(fov / 2)) / (1 - 0.08 * pageK)))
    : fov);

  // Descente de sortie (demande JB, 0.4.13) : en plus du dézoom, la caméra glisse vers le bas de l'écran, point de
  // visée compris, à mesure que la page descend — le labyrinthe remonte dans le cadre avec la page. Glissement
  // dans le plan de l'image et non en altitude : baisser la caméra la rapprocherait du sol, à rebours du dézoom.
  const DESCENTE = 0.22;   // part de la distance de visée parcourue quand le header est entièrement sorti
  const descD = new THREE.Vector3(), descR = new THREE.Vector3(), descU = new THREE.Vector3(), HAUT = new THREE.Vector3(0, 1, 0);
  function descente() {
    if (pageK <= 0) return;
    descD.subVectors(camLook, camPos);
    const dy = descD.length() * DESCENTE * pageK;
    descD.normalize();
    descR.crossVectors(descD, HAUT).normalize();
    descU.crossVectors(descR, descD);   // le haut de l'écran
    camPos.addScaledVector(descU, -dy);
    camLook.addScaledVector(descU, -dy);
  }

  // Respiration de l'écran de départ (0.4.101, demande JB) : une fois l'intro jouée, la vue globale tourne très
  // lentement autour du centre du 8, en va-et-vient (sinusoïde : ralentit et repart à chaque bout). Elle s'installe
  // en douceur, s'efface dès que la caméra quitte le départ et revient avec elle. Pas de mouvement pour qui l'a
  // demandé au système. Réglages d'essai : ?respiA=degrés (0 = coupée), ?respiP=secondes pour un aller-retour.
  const qsRespi = new URLSearchParams(location.search);
  const RESPI = {
    amp: THREE.MathUtils.degToRad(qsRespi.has('respiA') ? Number(qsRespi.get('respiA')) || 0 : 4),
    periode: (Number(qsRespi.get('respiP')) || 12) * 1000,
    montee: 4000,   // ms pour atteindre l'amplitude pleine après l'intro
    coupee: matchMedia('(prefers-reduced-motion: reduce)').matches || !!window.Shopify?.designMode,
  };
  let respiT0 = null;
  const respiD = new THREE.Vector3();
  // Coût (0.4.115, retour Arthur : saccades sur MacBook Air) : la respiration fait bouger la caméra en continu sur la vue
  // la plus lourde (tout le labyrinthe), qui était auparavant rendue une fois puis laissée au repos. Deux garde-fous :
  // ① quand elle est le seul mouvement, cadence plafonnée à 30 images/s (setFrameCap du moteur) — mouvement très lent ;
  // ② si la machine ne tient même pas ce rythme (p75 des écarts entre images rendues > 45 ms), la respiration s'arrête pour
  // la visite, en fondu : l'écran de départ redevient une image fixe, GPU au repos.
  const RESPI_CAP_MS = 33.4, RESPI_LENT_MS = 45, RESPI_FONDU = 2500;
  let respiStop = null, respiVerif = 0;
  // Option de la section (0.4.134, demande JB, désactivée par défaut) : sur ordinateur (souris), pas de respiration
  // au départ mais la parallaxe à la souris, comme sur les pièces, à la force réglée (100 % = celle des pièces).
  // Essai sans l'éditeur : ?paraD=0.5 force l'option et fixe la force.
  const DEPART_SOURIS = (labEl.dataset.departSouris === 'true' || qsRespi.has('paraD'))
    && matchMedia('(hover: hover) and (pointer: fine)').matches;
  const PARA_DEPART = qsRespi.has('paraD') ? Math.max(0, Number(qsRespi.get('paraD')) || 0)
    : Math.max(0, Number(labEl.dataset.departParallaxe) || 100) / 100;

  function respiration(u, now) {
    if (DEPART_SOURIS) { lab.setFrameCap?.(0); return; }
    if (RESPI.coupee || RESPI.amp <= 0 || !eveil.fini) { lab.setFrameCap?.(0); return; }
    if (respiT0 === null) respiT0 = now;
    const t = now - respiT0;
    let k = smoothstep(0.02, 0.004, Math.abs(shortestDelta(u, stops[0]))) * smoothstep(0, RESPI.montee, t);
    if (respiStop !== null) k *= 1 - smoothstep(respiStop, respiStop + RESPI_FONDU, now);
    // seule en mouvement : caméra posée sur le départ, page en haut, pas d'approche de fiche
    const seule = k > 0 && respiStop === null && Math.abs(scroll.target - scroll.pos) < 1e-6 && pageK <= 0 && dolly.k <= 0;
    lab.setFrameCap?.(seule ? RESPI_CAP_MS : 0);
    if (seule && now - respiVerif > 1000) {
      respiVerif = now;
      const ecarts = lab.renderGaps;
      if (ecarts && ecarts.length >= 30) {
        const tri = [...ecarts].sort((a, b) => a - b);
        const p75 = tri[Math.floor(tri.length * 0.75)];
        if (p75 > RESPI_LENT_MS) {
          respiStop = now;
          console.info(`[labyrinthe-8] respiration arrêtée : ${Math.round(p75)} ms par image au départ (machine trop juste)`);
        }
      }
    }
    if (k <= 0) return;
    respiD.subVectors(camPos, camLook).applyAxisAngle(HAUT, RESPI.amp * k * Math.sin((2 * Math.PI * t) / RESPI.periode));
    camPos.copy(camLook).add(respiD);
  }

  function cameraFrame(dt) {
    const now = performance.now();
    // aimant : inertie presque finie, plus de geste depuis SNAP_DELAY → cible ramenée sur l'arrêt le plus proche
    if (!scroll.snapped && now - scroll.lastInput > SNAP_DELAY && Math.abs(scroll.target - scroll.pos) < 2e-3) {
      scroll.target = scroll.pos + nearestStop(scroll.pos, stops).delta;
      scroll.snapped = true;
    }
    const diff = scroll.target - scroll.pos;
    const d = densiteRef ? densite(scroll.pos) : 0;
    // glissé à la souris en cours (0.4.126) : la caméra colle à la main, plus vite et plus raide qu'en marche
    const vitesse = scroll.scrub ? maxSpeed * scroll.scrub : maxSpeed, raideur = scroll.scrub ? GLISSE_RAIDEUR : APPROCHE;
    const maxStep = vitesse * dt * (d > 0 ? Math.min(1, (boucle * densiteRef) / d) : 1);
    const step = THREE.MathUtils.clamp(diff * (1 - Math.exp(-dt * raideur)), -maxStep, maxStep), minStep = MIN_SPEED * dt;
    scroll.pos = Math.abs(step) >= minStep ? scroll.pos + step
      : Math.abs(diff) <= minStep ? scroll.target : scroll.pos + Math.sign(diff) * minStep;

    const u = wrap(scroll.pos);
    const pose = route.poseAt(u);
    camPos.fromArray(pose.pos);
    camLook.fromArray(pose.look);
    if (dolly.k > 0 && dolly.center) camPos.lerp(dolly.center, 0.25 * dolly.k);   // approche avant la page produit
    if (!eveil.fini) {   // éveil : la caméra part en recul et plus haut, puis se pose
      const c = 1 - easeInOutCubic(eveilK());
      eveilD.subVectors(camPos, camLook);
      const d = eveilD.length();
      camPos.addScaledVector(eveilD, EVEIL.recul * c);
      camPos.y += d * EVEIL.hauteur * c;
    }
    respiration(u, now);
    descente();
    lab.setCamera(camPos.toArray(), camLook.toArray(), dezoom(pose.fov));

    themeFrame(u);

    // chaque pièce apparaît à l'approche de sa station (fondu + montée, pendant le mouvement seulement)
    let vue = 0;
    STATIONS.forEach((s, i) => {
      const k = smoothstep(0.035, 0.012, Math.abs(shortestDelta(u, stops[i + 1])));
      layer.setVisibility(s.id, k);
      vue = Math.max(vue, k);
    });
    // Parallaxe à la souris seulement quand on voit un produit (0.4.102, demande JB) : nulle sur l'écran de départ, en
    // marche entre deux pièces et pendant la remontée ; elle suit l'apparition de la pièce, sans à-coup.
    // option « parallaxe au départ » : elle vaut aussi sur l'écran de départ (0.4.134), une fois l'intro jouée
    const depart = DEPART_SOURIS && eveil.fini ? smoothstep(0.035, 0.012, Math.abs(shortestDelta(u, stops[0]))) * PARA_DEPART : 0;
    lab.setParallax?.(Math.max(vue, depart));
  }

  /* ---------- version noir ↔ blanc (spec § 5.1, sans le défilement) ----------
     La version ne suit que le parcours : blanche dès l'entrée dans les couloirs. Le défilement ne blanchit
     plus rien (l'en-tête a sa propre classe, voir onScroll). */
  function themeFrame(u) {
    mixer.set(route.themeAt(u));
    syncTon(mixer.level);   // après le mixeur, qui écrit `data-v` de son côté quand le niveau change
  }

  // Éveil : l'ambiance du moment (celle du mixeur) est rejouée à chaque image, lumières modulées ; à la fin,
  // l'ambiance pleine est posée une dernière fois et le mixeur reprend seul la main.
  function eveilFrame(dt = 0) {
    if (eveil.fini) return;
    if (eveil.t0 !== null) {
      if (eveil.ecoule === 0) introLog(`intro : 1re image (image de ${Math.round(dt * 1000)} ms)`);
      eveil.images = (eveil.images || 0) + 1;
      eveil.ecoule += Math.min(Number(dt) || 0, 1 / 10);   // 1/30 s étirait l'intro à 9 s à 8 images/s (relevé JB, 0.4.135)
    }
    if (eveil.t0 === null && !avecPreloader && stageEl.classList.contains('ready')) eveiller();
    const e = eveilK();
    const base = mixTheme(lab.T.dark, lab.T.light, mixer.level ** 1.5);
    if (e >= EVEIL.interface) interfacePrete();
    if (e >= 1) {
      introLog(`intro terminée : ${eveil.images} images en ${Math.round(performance.now() - eveil.t0)} ms (${Math.round((performance.now() - eveil.t0) / eveil.images)} ms par image)`);
      eveil.fini = true;
      interfacePrete();
      lab.applyTheme(base, { keepHistory: true });
      return;
    }
    const expo = smoothstep(0, 0.45, e), lumiere = smoothstep(0, 0.85, e), ciel = smoothstep(0.1, 0.9, e);
    lab.applyTheme({
      ...base,
      exposure: base.exposure * (EVEIL.veille + (1 - EVEIL.veille) * expo),
      skyI: base.skyI * (0.2 + 0.8 * ciel),
      lightI: base.lightI * lumiere,
      poolX: base.poolX + EVEIL.glisse * (1 - lumiere),
      poolR: base.poolR * (0.3 + 0.7 * lumiere),
      bloom: base.bloom * (1 + 1.6 * Math.sin(Math.PI * smoothstep(0.15, 0.85, e))),   // le halo au passage
    }, { keepHistory: true });
  }

  function setDolly(to) { Object.assign(dolly, { from: dolly.k, to, t0: performance.now(), active: true }); }

  function dollyFrame() {
    if (!dolly.active) return;
    const k = clamp01((performance.now() - dolly.t0) / dolly.dur);
    dolly.k = dolly.from + (dolly.to - dolly.from) * easeInOutCubic(k);
    if (k >= 1) dolly.active = false;
  }

  /* ---------- station « en vue » : celle dont le cartel et le bouton sont affichés ----------
     Deux seuils et un cliquet : pour ALLUMER il faut être dans la fenêtre ET en fin de course, sans quoi les
     stations traversées au milieu d'un saut feraient clignoter le cartel au passage ; pour ÉTEINDRE, ni l'un
     ni l'autre — le cartel tient jusqu'au bord de la fenêtre, puis s'attarde encore HOLD ms. */
  const NEAR = 0.022, ARRIVING = 0.03, HOLD = 300;
  let inView = -1, armed = -1, holdUntil = 0;
  // Pièce suivante / précédente demandée (flèches, points) : le cartel s'efface tout de suite, sans cliquet ni
  // rémanence, et ne revient qu'à l'arrivée sur la pièce visée (demande JB, 0.4.50).
  let enCommande = false;

  function couperCartel() {
    enCommande = true;
    armed = -1;
    holdUntil = 0;
  }

  function stationInView() {
    if (enCommande) {
      if (Math.abs(scroll.target - scroll.pos) > ARRIVING) return (inView = -1);
      enCommande = false;
    }
    const { index, delta } = nearestStop(scroll.pos, stops);
    const i = index - 1;
    if (index > 0 && Math.abs(delta) <= NEAR && (armed === i || Math.abs(scroll.target - scroll.pos) <= ARRIVING)) {
      armed = i; holdUntil = 0;
      return (inView = i);
    }
    if (armed >= 0) { armed = -1; holdUntil = performance.now() + HOLD; }   // la pièce suivante coupe court à la rémanence
    return (inView = performance.now() < holdUntil ? inView : -1);
  }

  tagEl.addEventListener('animationend', () => tagEl.classList.remove('swap'));

  // L'annonce de l'écran de départ : allumée tant que la caméra est sur l'étape zéro (vue globale), éteinte dès
  // qu'elle s'en éloigne ; elle revient quand la boucle repasse par l'écran de départ.
  const DEPART = 0.02;
  // Pagination (mobile) : un point par pièce ; l'écran de départ n'en a pas (aucun point allumé). Le point allumé
  // suit la CIBLE de la marche, pour répondre tout de suite au geste.
  let pointActif = -1;
  function pointsFrame() {
    if (!pointsEl) return;
    const k = nearestStop(scroll.target, stops).index - 1;   // -1 = écran de départ
    if (k === pointActif) return;
    pointActif = k;
    pointsEl.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-current', i === k ? 'step' : 'false'));
  }

  // La consigne ne sert qu'avant le premier geste latéral : dès que la caméra a quitté l'écran de départ pour une
  // pièce, elle ne revient plus (demande JB, 0.4.79). Le défilement vertical ne déplace pas la caméra : descendre
  // puis remonter sans avoir changé de pièce la laisse en place.
  let consigneLue = false;
  function annonceFrame() {
    pointsFrame();
    const { index, delta } = nearestStop(scroll.pos, stops);
    // dès que la marche VISE une pièce, sans attendre que la caméra ait quitté le départ (demande JB, 0.4.87)
    if (index !== 0 || nearestStop(scroll.target, stops).index !== 0) consigneLue = true;
    const depart = index === 0 && Math.abs(delta) <= DEPART && !blocked();
    // l'annonce et la consigne du centre vivent avec l'écran de départ (SDM.tasks.041)
    annonceEl?.classList.toggle('on', depart);
    consigneEl?.classList.toggle('on', depart && !consigneLue);
  }

  function labelFrame() {
    annonceFrame();
    const at = stationInView();
    // La pilule vit exactement comme le cartel (demande JB, 0.4.79) : elle paraît à l'arrivée sur une pièce et
    // s'efface avec lui dès qu'on la quitte, y compris d'une pièce à l'autre (fin de la règle de la 0.4.48).
    const i = blocked() ? -1 : at;
    setPill(i);
    if (i < 0) { tagEl.classList.remove('on'); return; }
    const s = STATIONS[i];
    if (tagEl.dataset.station !== s.id) {
      const visible = tagEl.classList.contains('on');
      tagEl.dataset.station = s.id;
      tagEl.innerHTML = (numero(s) ? `<p class="sdm-small">${escapeHtml(numero(s))}</p>` : '') + `<p class="nm">${escapeHtml(s.nom)}</p>`
        + (s.legende ? `<p class="tz">${escapeHtml(s.legende)}</p>` : '');
      // texte remplacé sous un cartel déjà affiché : relancer le fondu (reflow pour rejouer l'animation)
      if (visible) { tagEl.classList.remove('swap'); void tagEl.offsetWidth; tagEl.classList.add('swap'); }
    }
    tagEl.classList.add('on');
  }

  /* ---------- la pilule : le bouton de la pièce, masqué hors des pièces ---------- */
  let pilule;
  function setPill(i) {
    const s = i >= 0 ? STATIONS[i] : null;
    if ((s?.id ?? null) === pilule) return;
    pilule = s?.id ?? null;
    if (s) {
      ctaEl.textContent = s.cta || 'Découvrir';
      ctaEl.dataset.station = s.id;
      ctaEl.href = s.url || '#';
    }
    tocEl.classList.toggle('piece', !!s);
    tocEl.setAttribute('aria-label', s ? s.nom : tocEl.dataset.labelDefaut || 'La collection');
    ctaEl.inert = !s;
  }

  /* ---------- navigation ---------- */
  function goStation(i) { goStop(i + 1); }

  // Étape k de la boucle (0 = écran de départ, 1…n = les pièces), par le plus court chemin.
  function goStop(k) {
    if (k < 0 || !lab || blocked()) return;
    scroll.target = scroll.pos + shortestDelta(scroll.pos, stops[k]);
    scroll.snapped = true;
    scroll.lastInput = performance.now();
    couperCartel();
  }

  // Pièce suivante (dir = 1) ou précédente (dir = -1). On repart TOUJOURS dans le sens du geste, jamais par
  // le plus court chemin. La boucle compte l'étape zéro — l'écran de départ, la vue globale (arbitrage JB,
  // SDM.tasks.021) : après la dernière pièce, « suivant » s'y arrête avant de repartir vers la pièce I.
  function goStep(dir) {
    if (!lab || blocked() || !dir) return;
    const m = STATIONS.length + 1;   // 0 = écran de départ (vue globale), 1…n = les stations
    const { index } = nearestStop(scroll.pos, stops);
    const to = (index + dir + m) % m;
    let d = shortestDelta(scroll.pos, stops[to]);
    if (dir > 0 && d <= 0) d += 1;
    if (dir < 0 && d >= 0) d -= 1;
    scroll.target = scroll.pos + d;
    scroll.snapped = true;
    scroll.lastInput = performance.now();
    couperCartel();
  }

  // « Découvrir » : la fiche s'ouvre tout de suite par-dessus, la caméra ne bouge pas (0.4.105, demande JB ; avant,
  // elle s'approchait de la pièce pendant 0,7 s). `leaving` retient la navigation tant que la fiche est ouverte.
  // L'approche (dolly) reste dans le code, inutilisée.
  function enter(id) {
    const s = byId(id);
    if (!s) return;
    if (!lab || !layer) { go(s); return; }
    if (blocked()) return;
    leaving = true;
    go(s);
  }

  function go(s) {
    if (!s?.url) { leave(); return; }
    if (window.sdmFiche) window.sdmFiche.open(s.url).then((ok) => { if (!ok) location.assign(s.url); });
    else location.assign(s.url);
  }

  function leave() {
    if (!leaving) return;
    leaving = false;
    if (dolly.k > 0) setDolly(0);   // recul seulement s'il y a eu approche : sinon 1,6 s de navigation bloquée pour rien
  }
  document.addEventListener('sdm:fiche-close', leave);

  function escapeHtml(v) {
    return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Accès console (mise au point et tests). Un SEUL littéral, jamais un Object.assign : celui-ci exécuterait
  // les accesseurs de la source et figerait leur valeur du chargement.
  window.sdmLab = {
    STATIONS, scroll, goStation, goStep, enter,
    get engine() { return lab; },
    get station() { return inView; },
    get pilule() { return pilule; },
    get level() { return mixer?.level ?? 0; },
    get dolly() { return dolly.k; },
    get pageK() { return pageK; },
    get maxSpeed() { return maxSpeed; },
    set maxSpeed(v) { maxSpeed = Math.max(MIN_SPEED, Number(v) || maxSpeed); },
    get boucle() { return boucle; },
    set boucle(v) { boucle = Math.max(0.5, Number(v) || boucle); },
  };
}
